<template>
  <div class="space-y-4">
    <!-- Progress bar -->
    <div
      class="relative w-full h-2 bg-primary/20 overflow-hidden rounded-full"
      role="progressbar"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-valuenow="progress"
    >
      <div
        class="h-full bg-brand transition-all duration-300 ease-in-out"
        :style="{ width: `${progress}%` }"
      ></div>
    </div>

    <!-- Step circles -->
    <div class="grid gap-2" :style="{ gridTemplateColumns: gridTemplate }">
      <div
        v-for="(label, index) in stepLabels"
        :key="index"
        class="flex flex-col items-center gap-2 text-center"
        :class="{
          'text-foreground': isCurrent(index),
          'text-muted-foreground': isComplete(index),
          'text-muted-foreground/50': !isCurrent(index) && !isComplete(index),
        }"
      >
        <!-- Step circle -->
        <div
          class="flex w-8 h-8 items-center justify-center rounded-full border-2 text-sm font-medium transition-colors"
          :class="{
            'border-brand bg-brand text-white': isCurrent(index) || isComplete(index),
            'border-muted-foreground/30': !isCurrent(index) && !isComplete(index),
          }"
        >
          <template v-if="isComplete(index)">
            <!-- check icon -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2"
              stroke="currentColor"
              class="w-4 h-4"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </template>
          <template v-else>
            {{ index + 1 }}
          </template>
        </div>

        <!-- Step label -->
        <span class="text-xs font-medium hidden sm:block">{{ label }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  currentStep: number
  totalSteps: number
  stepLabels: string[]
}>()

const progress = computed(() => {
  const total = props.totalSteps || props.stepLabels.length || 1
  return ((props.currentStep + 1) / total) * 100
})

const columnCount = computed(() => props.stepLabels.length || props.totalSteps || 1)
const gridTemplate = computed(() => `repeat(${columnCount.value}, minmax(0, 1fr))`)

const isComplete = (index: number) => index < props.currentStep
const isCurrent = (index: number) => index === props.currentStep
</script>
